<template>
  <div class="customer-accounts-wrap">
    <div class="page-header">
      <div class="page-header__left">
        <div class="page-header__icon">
          <i class="el-icon-user"></i>
        </div>
        <h1 class="page-header__title">{{ $t('settings.customerAccounts') }}</h1>
      </div>

      <div class="page-header__actions">
        <base-button
          type="primary"
          icon="el-icon-plus"
          @click="$router.push({path: `/app/settings/${$route.params.id}/account/create`})">
          {{ $t('settings.createLocalAccount') }}
        </base-button>
      </div>
    </div>
    <div class="accounts-card">
      <div class="accounts-card__toolbar">
        <el-input
          class="toolbar-search"
          v-model="searchTerm"
          :placeholder="$t('settings.account.search')"
          prefix-icon="el-icon-search"
          clearable>
        </el-input>
      </div>

      <el-tabs tab-position="top" v-model="tabPosition" class="accounts-tabs">
        <el-tab-pane :label="$t('settings.remoteAccounts')">
          <customer-accounts-table
            :accounts="accountsPage.content"
            :loading="loading"
            @accountUpdate="patchAccount"
            @sendEmail="sendEmail"
            @sendPush="sendPush"
            @resetPushToken="resetPushToken"
            @toggleActive="toggleActive"
          ></customer-accounts-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('settings.localAccounts')">
          <customer-accounts-table
            :accounts="accountsPage.content"
            :show-local-accounts="true"
            :loading="loading"
            @accountUpdate="patchAccount"
            @delete="deleteAccount"
            @sendEmail="sendEmail"
            @sendPush="sendPush"
            @editAccount="editAccount"
            @resetPushToken="resetPushToken"
            @toggleActive="toggleActive"
          ></customer-accounts-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="pagination-bar" v-if="accountsPage">
      <el-pagination
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange"
        :current-page.sync="pagination.page"
        :page-sizes="[10, 20, 30, 100]"
        :page-size="accountsPage.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="accountsPage.totalElements">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {createNamespacedHelpers} from 'vuex';
import CustomerAccountsTable from './CustomerAccountsTable'
import {adminAccountService} from "@/services/AdminAccountService.class";

const mspStateMappers = createNamespacedHelpers('msp');
export default {
  name: "CustomerAccounts",
  components: {CustomerAccountsTable, BaseButton},
  data() {
    return {
      tabPosition: 0,
      searchTerm: '',
      customerId: null,
      accountsPage: {content: []},
      loading: true,
      pagination: {
        page: 1,
        pageSize: 10,
      }
    }
  },
  computed: {
    localOnly() {
      return this.tabPosition === '1';
    }
  },
  mounted() {
    this.customerId = this.$route.params.id;
    this.queryAccounts();
  },
  watch: {
    tabPosition() {
      this.accountsPage.content = [];
      this.queryAccounts();
    },
    searchTerm() {
      this.queryAccounts();
    },
    pagination() {
      this.queryAccounts()
    },
  },
  methods: {
    ...mspStateMappers.mapActions(['queryAccountsByCustomerId']),
    queryAccounts() {
      this.loading = true;
      const pagination = {
        page: this.pagination.page - 1,
        pageSize: this.pagination.pageSize,
      };
      this.queryAccountsByCustomerId({
        customerId: this.customerId,
        page: pagination,
        localOnly: this.localOnly,
        search: this.searchTerm
      }).then(result => {
        this.accountsPage = result.data;
        this.loading = false;
      })
    },
    deleteAccount(account) {
      this.$confirm(this.$t('settings.deleteAccountInfoText'), this.$t('settings.deleteAccount'), {
        confirmButtonClass: 'el-button--danger',
        confirmButtonText: this.$t('settings.deleteAccount'),
        cancelButtonText: this.$t('base.cancel'),
        type: 'warning'
      }).then(() => {
        this.loading = true;
        adminAccountService.deleteAccount(account.id).then(() => {
          this.loading = false;
          this.queryAccounts();
        });
      });

    },
    sendEmail(account) {
      adminAccountService.sendTestEmail(account.id).then(() => {
        this.$message.info(this.$t('settings.emailSentTo', {name: account.appLoginName}));
      })
    },
    sendPush(account) {
      adminAccountService.sendTestPush(account.id).then(() => {
        this.$message.info(this.$t('settings.pushSentTo', {name: account.appLoginName}));
      })
    },
    patchAccount(account) {
      adminAccountService.updateAccount(account.id, account).then(() => {
        this.$message({type: 'success', message: this.$t('settings.patchSuccess')});
      }).catch(() => {
        this.$message({type: 'error', message: this.$t('settings.genericHttpError')});
      });
    },
    editAccount(account) {
      this.$router.push({path: `/app/settings/${this.$route.params.id}/accounts/${account.id}`})
    },
    resetPushToken(account) {
      adminAccountService.deleteAllDeviceTokens(account.id)
    },
    handlePageChange() {
      this.queryAccounts();
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.queryAccounts();
    },
    toggleActive(payload) {
      this.loading = true;
      adminAccountService.setAccountActive(payload.id, payload.active)
        .then(() => {
          this.$message.success(
            payload.active
              ? this.$t('settings.account.activated')
              : this.$t('settings.account.deactivated')
          );
          this.queryAccounts();
        })
        .catch((error) => {
          console.error(this.$t('settings.accountStatusChangeError'), error);
          this.$message.error(this.$t('settings.genericHttpError'));
          payload.active = !payload.active;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
}
</script>

<style lang="scss">
.accounts-tabs {
  .el-tabs__header {
    margin-bottom: 0;
    padding: 0 20px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-tabs__nav-wrap::after {
    display: none;
  }

  .el-tabs__item {
    font-size: 13.5px;
    font-weight: 500;
    color: #909399;
    height: 46px;
    line-height: 46px;
    padding: 0 20px;

    &.is-active {
      color: #409EFF;
      font-weight: 600;
    }
  }

  .el-tabs__active-bar {
    height: 2px;
    border-radius: 2px;
  }

  .el-tabs__content {
    padding: 0;
  }
}
</style>

<style scoped lang="scss">
$border: #e8e8ee;
$text-primary: #1a1a2e;

.customer-accounts-wrap {
  min-width: 0;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__icon {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background: linear-gradient(135deg, #409EFF 0%, #2d7dd2 100%);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      color: #fff;
      font-size: 17px;
    }
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: $text-primary;
  }
}

.accounts-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid $border;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;

  &__toolbar {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid $border;
    gap: 12px;
  }
}

.toolbar-search {
  max-width: 320px;

  :deep(.el-input__inner) {
    border-radius: 8px;
    height: 36px;
    font-size: 13px;
  }
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 4px 0;

  :deep(.el-pagination) {
    .el-pager li {
      border-radius: 6px;
    }

    button {
      border-radius: 6px;
    }
  }
}
</style>
